import { Link } from "react-router-dom";
import { Crosshair, Wind, ClipboardList, Lightbulb, Video, UserPlus, LineChart, ArrowRight, Trophy, Crown, Gem } from "lucide-react";

const Section = ({ id, eyebrow, title, children, testid }) => (
  <section id={id} className="relative py-24 md:py-32 border-t border-[#1E281E]" data-testid={testid}>
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight max-w-3xl">{title}</h2>
      {children}
    </div>
  </section>
);

const FEATURES = [
  [Crosshair, "Batting Analysis", "Planned video-based observations of stance, balance, shot execution, and technique."],
  [Wind, "Bowling Analysis", "Planned observations of bowling action, approach, alignment, and delivery mechanics where supported."],
  [ClipboardList, "Detailed Performance Reports", "Structured observations, strengths, potential weaknesses, and practical recommendations."],
  [Lightbulb, "Personalized Improvement Insights", "Training guidance based on supported analysis capabilities."],
];

export const Features = () => (
  <Section id="features" eyebrow="Features · In development" title="Every Ball Has Something to Teach You." testid="features-section">
    <div className="mt-14 grid md:grid-cols-2 gap-5">
      {FEATURES.map(([I, t, d], i) => (
        <div key={t} className={`card-surface card-hover p-8 ${i === 0 ? "md:row-span-1 md:min-h-[240px]" : ""}`} data-testid={`feature-card-${i}`}>
          <div className="w-12 h-12 rounded-xl bg-[#B7FF3C]/10 border border-[#B7FF3C]/25 flex items-center justify-center"><I className="text-[#B7FF3C]" size={22} /></div>
          <h3 className="mt-6 text-2xl font-bold uppercase">{t}</h3>
          <p className="mt-2 text-[#A3ADA3] leading-relaxed">{d}</p>
          <span className="inline-block mt-5 font-mono text-[10px] tracking-widest text-[#7C8A7C] uppercase">Planned feature</span>
        </div>
      ))}
    </div>
    <p className="mt-8 text-sm text-[#7C8A7C] max-w-2xl">CricAnalyze is being built. Features are described as planned and may change. We do not claim professional-grade or biomechanical accuracy.</p>
  </Section>
);

const STEPS = [
  [Video, "Record Your Game", "Capture a batting or bowling session using your phone or camera."],
  [UserPlus, "Join CricAnalyze", "Register for the waitlist and receive updates about early access."],
  [LineChart, "Analyze and Improve", "When the product launches, upload supported videos and explore the available analysis reports."],
];

export const HowItWorks = () => (
  <Section id="how-it-works" eyebrow="How it works" title="From Video to Valuable Insights." testid="how-it-works-section">
    <div className="mt-14 grid md:grid-cols-3 gap-5">
      {STEPS.map(([I, t, d], i) => (
        <div key={t} className="card-surface card-hover p-8 relative overflow-hidden" data-testid={`step-card-${i + 1}`}>
          <span className="absolute -right-2 -top-6 font-display text-[120px] font-black text-[#B7FF3C]/[0.06] leading-none">0{i + 1}</span>
          <span className="font-mono text-sm text-[#B7FF3C]">STEP 0{i + 1}</span>
          <I className="mt-6 text-white" size={26} />
          <h3 className="mt-4 text-2xl font-bold uppercase">{t}</h3>
          <p className="mt-2 text-[#A3ADA3] leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
    <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
      <Link to="/join" data-testid="how-get-early-access-btn" className="group self-start px-7 py-3.5 rounded-full bg-[#B7FF3C] text-[#080A08] font-bold flex items-center gap-2 hover:bg-[#c9ff6a] transition-colors">
        Get Early Access <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
      </Link>
      <p className="text-sm text-[#7C8A7C]">Joining the waitlist does not automatically enable video uploads.</p>
    </div>
  </Section>
);

const ICONS = { standard: Trophy, premium: Crown, exclusive: Gem };

export const RewardsSection = ({ tiers = [] }) => (
  <Section id="rewards" eyebrow="Referral rewards" title="Invite Your Team. Unlock Rewards." testid="rewards-section">
    <p className="mt-5 max-w-2xl text-[#A3ADA3]">Every verified waitlist member gets a private referral link. A referral counts only when a new person joins through your link and verifies their email. Your highest tier applies — rewards don't stack.</p>
    <div className="mt-12 grid md:grid-cols-3 gap-5">
      {tiers.map((t, i) => {
        const I = ICONS[t.key];
        return (
          <div key={t.key} className={`card-surface card-hover p-8 ${i === 2 ? "border-[#B7FF3C]/40 lime-glow" : ""}`} data-testid={`reward-tier-${t.key}`}>
            <div className="flex items-center justify-between"><I className="text-[#B7FF3C]" /><span className="font-mono text-xs text-[#7C8A7C]">{t.threshold} REFERRALS</span></div>
            <h3 className="mt-6 text-3xl font-black uppercase">{t.name}</h3>
            <p className="mt-2 text-white">{t.reward}</p>
            <p className="mt-1 text-sm text-[#7C8A7C]">Duration: {t.duration}</p>
          </div>
        );
      })}
    </div>
    <p className="mt-8 text-sm text-[#7C8A7C]">Rewards are unlocked and held for redemption at launch. Read the full <Link to="/terms" className="text-[#B7FF3C] underline-offset-4 hover:underline">reward terms</Link>. There is no public leaderboard — your stats stay private.</p>
  </Section>
);
